import {
  AfterViewInit,
  ChangeDetectionStrategy,
  Component,
  ElementRef,
  OnDestroy,
  Renderer2,
  SecurityContext,
  ViewChild,
  computed,
  inject,
  signal
} from '@angular/core';
import { DomSanitizer } from '@angular/platform-browser';
import {
  Middleware,
  Placement,
  autoPlacement,
  autoUpdate,
  computePosition,
  flip,
  offset,
  shift,
  size
} from '@floating-ui/dom';
import { TranslatePipe } from '@ngx-translate/core';
import { Subject, Subscription, debounceTime } from 'rxjs';

import { isNil } from '@gfe/core/utils/shared-utils';

import { clipReferenceToVisible, isDisconnectedTarget } from './tooltip-target.utils';

export type TooltipMiddleware = 'flip' | 'shift' | 'autoPlacement' | 'offset';
type TooltipType = 'default' | 'info' | 'expand-details';

export interface TooltipOptions {
  // Should be used with content to provide a title to the tooltip at the top
  title?: string;
  /**
   * A trailing part of the title that is never truncated, whatever the title's own length does.
   * For a title made of an identifier plus a word naming what it is, where the word is the half
   * a reader cannot reconstruct.
   */
  titleSuffix?: string;
  position?: Placement;
  middlewares?: TooltipMiddleware[];
  maxWidth?: number;
  minWidth?: number;
  showHint?: boolean;
  hintText?: string;
  x?: number;
  y?: number;
  offset?: number;
  // Allow over the tooltip, select the text and do click actions or any other interaction.
  interactive?: boolean;
  // Should only be used with interactive tooltips. When used, the content will be used as a summary.
  details?: string;
}

@Component({
  selector: 'app-tooltip-singleton',
  templateUrl: './tooltip-singleton.component.html',
  changeDetection: ChangeDetectionStrategy.OnPush,
  standalone: true,
  imports: [TranslatePipe]
})
export class TooltipSingletonComponent implements AfterViewInit, OnDestroy {
  @ViewChild('tooltipSingleton') tooltip!: ElementRef<HTMLDivElement>;
  title = signal<string>('');
  titleSuffix = signal<string>('');
  content = signal<string>('');
  // only used for interactive tooltips
  details = signal<string>('');
  showHint = signal<boolean>(false);
  hintText = signal<string>('');
  maxWidth = signal<number>(0);
  minWidth = signal<number>(0);
  interactive = signal<boolean>(false);
  isOverTooltip = signal<boolean>(false);
  tooltipType = computed<TooltipType>(() => {
    if (!isNil(this.details()) && this.details().length > 0) {
      return 'expand-details';
    }
    return !isNil(this.title()) && this.title().length > 0 ? 'info' : 'default';
  });
  showDetails = signal<boolean>(false);
  private closeEvent = new Subject<boolean>();
  private subscription: Subscription | undefined;

  classes = computed(() => {
    if (this.tooltipType() === 'default') {
      return `${this.maxWidth() === 0 ? 'size-max' : null} rounded bg-zinc-800 px-2 py-1 text-xs font-normal text-zinc-200 opacity-0 shadow-md dark:bg-zinc-50 dark:text-zinc-900 inline-block`;
    } else {
      return `${this.maxWidth() === 0 ? 'size-max' : null} rounded-lg border border-zinc-600 bg-zinc-800 px-3 py-2 text-sm text-zinc-200 opacity-0 shadow-lg dark:border-zinc-300 dark:bg-zinc-50 dark:text-zinc-900 inline-flex flex-col`;
    }
  });

  private sanitizer = inject(DomSanitizer);
  private renderer = inject(Renderer2);
  private position: Placement | undefined;
  private defaultMiddlewares: TooltipMiddleware[] = ['offset', 'flip', 'shift'];

  private cleanupAutoUpdate?: () => void;

  show(target: HTMLElement, content: string, opts: TooltipOptions = {}): void {
    this.closeEvent.next(false);
    const sanitizedContent = this.sanitizedContent(content);
    const details = this.sanitizedContent(opts.details ?? '');
    const title = this.sanitizedContent(opts.title ?? '');
    const hintText = this.sanitizedContent(opts.hintText ?? '');

    this.content.set(sanitizedContent);
    this.details.set(details);
    this.title.set(title);
    this.titleSuffix.set(this.sanitizedContent(opts.titleSuffix ?? ''));
    this.hintText.set(hintText);
    this.showHint.set(opts.showHint ?? false);
    this.maxWidth.set(opts.maxWidth ?? 0);
    this.minWidth.set(opts.minWidth ?? 0);

    this.position = opts.position;

    const middlewareConfig: TooltipMiddleware[] =
      (opts.middlewares ?? []).length > 0
        ? (opts.middlewares as TooltipMiddleware[])
        : this.defaultMiddlewares;
    const middleware = [
      clipReferenceToVisible(),
      ...middlewareConfig.map((m) => this.getMiddleware(m, opts))
    ];

    const tooltip = this.tooltip.nativeElement;

    // Both caps are cleared as well as set: one element serves every tooltip in the app, so a cap
    // left behind by the last caller would go on clamping every later tooltip that asks for none —
    // where `size-max` alone is supposed to let it grow to its content.
    tooltip.style.maxHeight = '';
    tooltip.style.maxWidth = this.maxWidth() > 0 ? `${this.maxWidth()}px` : '';
    middleware.push(
      size({
        padding: 5,
        apply: ({ availableWidth, availableHeight }) => {
          // Any tooltip can wrap a line too long for the screen, but only an interactive one can
          // be hovered, so only there can a body too tall for it be scrolled.
          const requestedWidth = this.maxWidth() > 0 ? this.maxWidth() : Infinity;
          tooltip.style.maxWidth = `${Math.max(0, Math.min(requestedWidth, availableWidth))}px`;
          if (opts.interactive) {
            tooltip.style.maxHeight = `${Math.max(0, availableHeight)}px`;
          }
        }
      })
    );
    tooltip.querySelectorAll('.overflow-y-auto').forEach((el) => (el.scrollTop = 0));

    tooltip.style.opacity = '1';
    tooltip.style.minWidth = `${this.minWidth()}px`;

    if (opts.interactive) {
      this.interactive.set(true);
    } else {
      this.interactive.set(false);
    }

    if (!isNil(opts.x) && !isNil(opts.y)) {
      Object.assign(tooltip.style, {
        left: `${opts.x}px`,
        top: `${opts.y}px`
      });
      return;
    }

    this.cleanupAutoUpdate?.();
    // Live update position while scrolling, resizing, dragging, etc.
    this.cleanupAutoUpdate = autoUpdate(target, tooltip, () => {
      // When the target is removed from the DOM (e.g. a button that triggers
      // route navigation), autoUpdate's observers can still fire one last time.
      // Positioning against a detached element resolves to (0,0)-ish, parking
      // the tooltip in a screen corner. Hide instead of repositioning.
      // Note: virtual reference elements (e.g. the mapbox marker tooltip) are
      // plain objects with no `isConnected`, so they must never be treated as
      // disconnected — see isDisconnectedTarget.
      if (isDisconnectedTarget(target)) {
        this.hideTooltip();
        return;
      }
      computePosition(target, tooltip, {
        placement: this.position,
        middleware,
        strategy: 'fixed'
      }).then(({ x, y }) => {
        Object.assign(tooltip.style, {
          left: `${x}px`,
          top: `${y}px`
        });
      });
    });
  }

  hide() {
    if (this.isOverTooltip()) {
      this.closeEvent.next(false);
      return;
    }
    if (this.interactive()) {
      this.closeEvent.next(true);
    } else {
      this.hideTooltip();
    }
  }

  updateContent(content: string, title?: string, hintText?: string) {
    this.content.set(this.sanitizedContent(content));
    this.title.set(this.sanitizedContent(title ?? ''));
    this.hintText.set(this.sanitizedContent(hintText ?? ''));
  }

  ngAfterViewInit(): void {
    this.renderer.listen(this.tooltip.nativeElement, 'mouseenter', () => {
      this.isOverTooltip.set(true);
      this.closeEvent.next(false);
    });
    this.renderer.listen(this.tooltip.nativeElement, 'mouseleave', () => {
      this.isOverTooltip.set(false);
      this.closeEvent.next(true);
    });

    this.subscription = this.closeEvent.pipe(debounceTime(200)).subscribe((value) => {
      if (this.interactive() && value && !this.isOverTooltip()) {
        this.hideTooltip();
      }
    });
  }

  ngOnDestroy(): void {
    this.clean();
    this.subscription?.unsubscribe();
  }

  toggleDetails(): void {
    this.showDetails.set(!this.showDetails());
  }

  private hideTooltip() {
    const tooltip = this.tooltip.nativeElement;
    tooltip.style.opacity = '0';
    this.clean();
  }

  private sanitizedContent(content: string) {
    return this.sanitizer.sanitize(SecurityContext.HTML, content) ?? '';
  }

  private clean(): void {
    if (this.cleanupAutoUpdate) {
      this.cleanupAutoUpdate();
      this.cleanupAutoUpdate = undefined;
    }
    this.isOverTooltip.set(false);
    this.interactive.set(false);
    this.showDetails.set(false);
  }

  private getMiddleware(middleware: TooltipMiddleware, opts: TooltipOptions): Middleware {
    switch (middleware) {
      case 'flip':
        return flip();
      case 'shift':
        return shift({ padding: 5 });
      case 'autoPlacement':
        return autoPlacement();
      case 'offset':
        return offset(opts.offset ?? 6);
    }
  }
}
